
import Vue from 'vue';
import { Podcast } from '@root/modules/podcast/types/podcast';
import ContentImage from '@core/components/UI/ContentImage.vue';
import SharePopup from '@root/modules/podcast/components/fragments/SharePopup.vue';
import buildPodcastUrl from '@root/modules/podcast/utils/buildPodcastUrl';
import { CustomerData } from '@root/modules/customer/types/customer';
import getDurationString from '@root/modules/podcast/utils/getDurationString';
import { AudioItem } from '@root/modules/podcast/types/player';
import isMobileView from '@root/common/utils/isMobileView';
import { faArrowUpFromBracket, faPlus, faTimes } from '@delfi.lt/fa-kit/icons/modules/classic/regular';
import { faChartSimple, faPause, faPlay, faLock } from '@delfi.lt/fa-kit/icons/modules/classic/solid';

interface ShNavigator extends Navigator {
  share: (data?: ShareData) => Promise<void>;
}

interface ShWindow extends Window {
  [index: string]: any;
  navigator: ShNavigator;
}

declare let window: ShWindow;

interface Props {
  item: Podcast;
  isBook: boolean;
  autoPlay: boolean;
}

interface Methods {
  togglePlay: () => void;
  share: () => Promise<void>;
  addToPlaylist: () => void;
  removeFromPlaylist: () => void;
  getDurationString: typeof getDurationString;
}

interface Computed {
  cropperData: PictureSrcset;
  currentlyPlaying: AudioItem | null;
  customerToken: string | null;
  hasChannelAccess: boolean;
  playlistData: CustomerData[];
  isPlaying: boolean;
}

interface Data {
  showSharePopup: boolean;
}

interface Srcset {
  w?: number;
  h?: number;
  r?: string;
}

interface PictureSrcset {
  xs: Srcset;
  sm: Srcset;
  lg: Srcset;
}

const cropperData: PictureSrcset = { lg: { w: 140, h: 140 }, sm: { w: 140, h: 140 }, xs: { w: 70, h: 70 } };
const bookCropperData: PictureSrcset = { lg: { w: 140, h: 210 }, sm: { w: 140, h: 210 }, xs: { w: 70, h: 105 } };

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ContentImage,
    SharePopup,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isBook: {
      type: Boolean,
      required: false,
      default: false,
    },
    autoPlay: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showSharePopup: false,
      faArrowUpFromBracket,
      faPlus,
      faTimes,
      faChartSimple,
      faPause,
      faPlay,
      faLock,
    };
  },
  computed: {
    cropperData() {
      return this.isBook ? bookCropperData : cropperData;
    },
    currentlyPlaying() {
      return this.$player?.state?.currentlyPlaying;
    },
    customerToken() {
      return this.$store.state.piano.token;
    },
    hasChannelAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    playlistData() {
      return this.$store.state.podcast.playlistData;
    },
    isPlaying() {
      return this.$player?.state?.isPlaying && this.$player?.state?.currentlyPlaying?.articleId === this.item.id;
    },
  },
  methods: {
    async togglePlay() {
      const episode = this.item;

      // Pause if item is currently playing
      if (this.isPlaying) {
        this.$player.pause();

        return;
      }

      if (episode.content.paywall.enabled && !this.hasChannelAccess) {
        this.$store.dispatch('podcast/setShowPaywall', true);

        // Retry togglePlay if customer hasAccess
        this.$nuxt.$on('podcast-paywall-close', () => {
          this.togglePlay();
        });

        return;
      }

      await this.$player.setup(episode);
    },
    async share() {
      if (!isMobileView()) {
        this.showSharePopup = true;
        return;
      }

      const episode = this.item;

      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      const title = episode.content.title.text;
      const text = '';
      const url = buildPodcastUrl({ article: episode, domain: 'www.delfi.lt/fone', fakeDomainEnabled });

      if (window.navigator.share) {
        await window.navigator.share({
          title,
          text,
          url,
        });
      } else if (window.AndroidWebAppMobileAPI && window.AndroidWebAppMobileAPI.sharePage) {
        window.AndroidWebAppMobileAPI.sharePage(url, title, text);
      }
    },
    async addToPlaylist() {
      if (!this.customerToken) {
        this.$store.dispatch('piano/showLoginModal', { screen: 'login', callback: () => setTimeout(() => this.addToPlaylist(), 500) });

        return;
      }

      const episode = this.item;

      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.$store.dispatch('podcast/addToPlaylist', episode.id);

      this.$alertHandler.addAlert({
        type: 'success',
        title: this.$t('podcast.alerts.addToPlaylist') as string,
        link: { href: 'https://www.delfi.lt/fone/mano-grojarastis', text: 'Mano grojaraštis' },
        fadeTrigger: 5000,
        closeButton: false,
        options: {
          key: 'podcast-add-to-playlist',
        },
      });
    },
    async removeFromPlaylist() {
      const episode = this.item;

      await this.$store.dispatch('podcast/setCustomerToken', this.customerToken);
      await this.$store.dispatch('podcast/removeFromPlaylist', episode.id);

      this.$alertHandler.addAlert({
        type: 'info',
        title: this.$t('podcast.alerts.removeFromPlaylist') as string,
        fadeTrigger: 5000,
        closeButton: false,
        options: {
          key: 'podcast-remove-from-playlist',
        },
      });
    },
    getDurationString,
  },
  mounted() {
    if (this.autoPlay) {
      this.$nextTick(() => {
        document.querySelector(`.podcast-episode-item[data-id="${this.item.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.togglePlay();
      });
    }
  },
});
